/**
 * PATH: packages/ui/src/components/dashboard-layout/GlobalShortcutBindings.tsx
 * FILE_ID: UI-DASHBOARD-GLOBAL-SHORTCUTS-001
 * PURPOSE: Register the app-shell global shortcuts that have MIGRATED onto the
 *          central dispatcher (ShortcutProvider). Renders nothing — it only
 *          calls useShortcut, so it must be a child of <ShortcutProvider>.
 * WHY: Phase 2 of the universal-shortcuts rollout moves global keydown handling
 *      off useLayoutState's ad-hoc capture listener and onto the dispatcher, so
 *      the shortcut respects user remap/disable overrides at dispatch time.
 *
 *      Phase 2a (this file) migrates ONLY the settings toggle (⌘, /
 *      app.settings.toggle). ⌘F / ⌘K (single-⌘F authority — capture-phase,
 *      beats browser Find) and ⌘I (AI sidebar — no descriptor yet) stay on the
 *      legacy capture handler in useLayoutState until Phase 2b adds capture
 *      dispatch + their descriptors. Only actions registered here are dispatched
 *      (the provider ignores keys it has no handler for), so un-migrated chords
 *      keep their existing behavior with zero double-firing.
 *
 * FLOW:
 *   props (onOpenSettings, settingsEnabled) → useShortcut('app.settings.toggle')
 *   → dispatcher fires → onOpenSettings()
 *
 * RELATED:
 *   - ../../shortcuts/ShortcutProvider: useShortcut (no-op outside a provider)
 *   - ./useLayoutState: owns the un-migrated capture handler + openSettings
 */

import { useShortcut } from '../../shortcuts/ShortcutProvider'

export interface GlobalShortcutBindingsProps {
  /** Open the settings modal — wired to ⌘, (app.settings.toggle). */
  onOpenSettings: () => void
  /** Only bind the settings chord when the host actually renders a settings modal. */
  settingsEnabled: boolean
}

/**
 * GlobalShortcutBindings — declarative registration of migrated app-shell
 * shortcuts. Returns null; mount inside <ShortcutProvider>.
 */
export function GlobalShortcutBindings({
  onOpenSettings,
  settingsEnabled,
}: GlobalShortcutBindingsProps) {
  useShortcut('app.settings.toggle', () => onOpenSettings(), { enabled: settingsEnabled })
  return null
}
